import { AxiosError } from 'axios';
import dayjs from 'dayjs';
import '@/styles/pages/CareerHistory/Edit.scss';

import React, { useState, useEffect, useRef, useMemo } from 'react';
import { FieldValues, useForm, UseFormProps, UseFormReturn, SubmitHandler } from 'react-hook-form';
import { useHistory, useParams } from 'react-router-dom';

import * as CH from '@/components/CareerHistory';
import {
  FormContainer,
  FormContainerTextArea,
  FormContainerTextfield,
  FormLabel,
} from '@/components/common/Form/';
import { AdminPage } from '@/componentsAdmin/Layout/Page';
import { Column } from '@/components/common/Page';
// import { HelmetContainer } from '@/components/common/Head/Helmet';
// import Footer from '@/components/common/Footer';
// import Header from '@/components/common/Header';
import { TitleSectionTitle } from '@/components/common/Title/SectionTitle';
import HeaderTitle from '@/components/common/Title/HeaderTitle';
import { NotificationMatchbou } from '@/components/common/Notification/Matchbou';
import BaseButton from '@/components/common/Button/BaseButton';
import { CAREER_HISTORY_TEMPLATE } from '@/definition/CAREER_HISTORY_TEMPLATE';
import { MESSAGE } from '@/definitionAdmin/MESSAGE';
import { careerHistory, qualification, career } from '@/hooksAdmin/usePageCareerHistory';
import {
  CareerHistory,
  ResponseError,
  Project,
  UserInfo,
  Status,
  ADMINApi,
} from '@/utils/api-client';
import { useDispatch } from 'react-redux';
import { toggleLoading, notificationError, notificationSuccess } from '@/reduxAdmin/modules/utils';

const beforeUnloadHandler = (event: any) => {
  event.returnValue = MESSAGE.before_unload;
};

export const AdminPagesCvEdit = (): React.ReactElement => {
  const dispatch = useDispatch();
  const descriptionRef = useRef<HTMLDivElement>(null);
  const [CHData, setCHData] = useState<(CareerHistory & Status & UserInfo) | undefined>();
  const [updatedKey, setUpdatedKey] = useState<string[]>([]);
  const [isFirstGet, setIsFirstGet] = useState<boolean>(false);
  const [toggleCareers, setToggleCareers] = useState<string[]>([]);
  const [deleteCareers, setDeleteCareers] = useState<string[]>([]);
  const [deleteProjects, setDeleteProjects] = useState<string[]>([]);
  const [toggleQualifications, setToggleQualifications] = useState<string[]>([]);
  const [deleteQualifications, setDeleteQualifications] = useState<string[]>([]);
  const [deleteskills, setDeleteskills] = useState<number[]>([]);
  const {
    data: fetchData,
    user,
    setDefaultValue,
    patch: patchCareerHistoryImmediately,
    deleteSkill,
    postQualification,
    postCareer,
    removeCareer,
    deleteQualification,
  } = careerHistory(true);
  const { patch: patchToggleQualification } = qualification();
  const { patch, removeProject } = career();
  const history = useHistory();
  const params = useParams<{ matchbox_id: string }>();
  const matchboxId = useMemo(() => params.matchbox_id, [params]);
  const [notification, setNotification] = useState<boolean>(true);

  const checkInputAgencyStatus = async () => {
    if (!matchboxId) return;
    const res = await new ADMINApi().getAdminInputAgency(matchboxId);
    if (res.data && res.data.status !== 1) {
      history.push(`/admin/users/${matchboxId}`);
    }
  };

  useEffect(() => {
    // 遷移して初回のみ取得する
    if (fetchData && !isFirstGet) {
      checkInputAgencyStatus();
      setCHData(fetchData);
      setIsFirstGet(true);
    }
  }, [fetchData]);

  const useFormProps: UseFormProps = {
    mode: 'onBlur',
    // FormContainerはfetchDataを監視しているが、fetchDataそのものをsetValueしないので、
    // defaultValuesを更新する必要がある
    defaultValues: setDefaultValue(CHData),
  };

  const methods = useForm(useFormProps);

  const templateMethods = useForm<{ template: string }>({
    defaultValues: {
      template: CAREER_HISTORY_TEMPLATE.template_job[0].value,
    },
  });

  useEffect(() => {
    const subscription = templateMethods.watch((value, { name }) => {
      if (!name || !descriptionRef.current) return;
      descriptionRef.current.textContent = CAREER_HISTORY_TEMPLATE.template[value[name]];
    });
    return () => subscription.unsubscribe();
  }, [templateMethods.watch]);

  const placeholder = {
    advantage: `例）
①Webサイト構築およびアプリ開発におけるUI/UX設計（主にFigmaを使用）
②ラフ案およびワイヤーフレームの作成（主にAdobe XD、Photoshop、Illustratorを使用）
③フロントエンド領域でのコーディング（主にHTML、CSS、JavaScript、PHPを使用）`,
  };

  const setBeforeUnload = (flag: boolean) => {
    if (flag) {
      window.addEventListener('beforeunload', beforeUnloadHandler);
    } else {
      window.removeEventListener('beforeunload', beforeUnloadHandler);
    }
  };

  const isInclude = (name: string) => {
    return updatedKey.some((key) => key.includes(name));
  };

  const onSubmit: SubmitHandler<any> = async (formData, e) => {
    const today = dayjs().format('YYYY-MM-DD');
    const data: any = {
      latest_update: today,
    };
    dispatch(toggleLoading(true));
    try {
      // 更新があったkeyのみを変換して利用する
      Object.keys(formData).forEach(function (key) {
        if (!updatedKey.some((uKey) => uKey.includes(key))) return;
        data[key] = formData[key];
      });

      // 資格・免許
      if (
        (isInclude('qualifications') || toggleQualifications.length !== 0) &&
        formData.qualifications
      ) {
        for (let i = 0; i < formData.qualifications?.length; i++) {
          const qualification = formData.qualifications[i];
          if (!qualification.q_id) {
            // 登録処理
            const res = await postQualification(qualification);
            formData.qualifications[i] = res?.data.qualifications[0];
          } else if (
            isInclude(`qualifications.${i}`) ||
            toggleQualifications.includes(qualification.q_id)
          ) {
            // 更新処理
            await patchToggleQualification(qualification, false, false, user?.user_id);
          }
        }
        data.qualifications = formData.qualifications;
      }

      // 職歴
      if ((isInclude('careers') || toggleCareers.length !== 0) && formData.careers) {
        for (let i = 0; i < formData.careers?.length; i++) {
          const career = formData.careers[i];
          if (career.c_employee_count === '') career.c_employee_count = 0;
          if (career.c_capital === '') career.c_capital = 0;
          career.c_projects.forEach((project: Project) => {
            if (!project.id) delete project.id;
          });
          if (!career.c_id) {
            // 登録処理
            const res = await postCareer(career);
            formData.careers[i] = res?.data.careers[0];
          } else if (isInclude(`careers.${i}`) || toggleCareers.includes(career.c_id)) {
            // 更新処理
            const res = await patch(career, user?.user_id);
            formData.careers[i] = res?.data.careers[0];
          }
        }
        data.careers = formData.careers;
      }

      // 職務経歴書更新
      await patchCareerHistoryImmediately(data, false);

      // 職歴削除処理
      if (deleteCareers.length !== 0) {
        for (let i = 0; i < deleteCareers.length; i++) {
          await removeCareer(deleteCareers[i]);
        }
        setDeleteCareers([]);
      }

      // 職歴・プロジェクト削除処理
      if (deleteProjects.length !== 0) {
        for (let i = 0; i < deleteProjects.length; i++) {
          await removeProject(Number(deleteProjects[i]));
        }
        setDeleteProjects([]);
      }

      // 資格・免許削除処理
      if (deleteQualifications.length !== 0) {
        for (let i = 0; i < deleteQualifications.length; i++) {
          await deleteQualification(deleteQualifications[i]);
        }
        setDeleteQualifications([]);
      }

      // スキル削除処理
      if (deleteskills.length !== 0) {
        for (let i = 0; i < deleteskills.length; i++) {
          await deleteSkill(deleteskills[i]);
        }
        setDeleteskills([]);
      }

      if (user?.user_id) {
        const res = await new ADMINApi().getAdminInputAgencyCareerHistory(user?.user_id);
        setCHData(res.data);
      }

      setToggleQualifications([]);
      setToggleCareers([]);
      setUpdatedKey([]);
      setBeforeUnload(false);
      dispatch(notificationSuccess(MESSAGE.saving_completed));
      // 確認ボタンの場合はプレビューに進む
      const event = e as React.BaseSyntheticEvent<Record<string, unknown>, any, any> | undefined;
      const button = event?.nativeEvent?.submitter as HTMLButtonElement;
      if (button.textContent === '反映を確認する')
        history.push(`/admin/transcription/${matchboxId}`);
    } catch (e) {
      // ロールバック処理
      if (user?.user_id) {
        const res = await new ADMINApi().getAdminInputAgencyCareerHistory(user?.user_id);
        const response = res.data;
        // 資格・免許
        if (response.qualifications && response.qualifications.length !== 0) {
          // 既に登録されていれば削除
          const reQualifications = response.qualifications;
          for (let i = 0; i < reQualifications.length; i++) {
            if (
              CHData?.qualifications &&
              CHData.qualifications.filter(
                (qualification) => qualification.q_id === reQualifications[i].q_id
              ).length === 0
            ) {
              await deleteQualification(reQualifications[i].q_id);
            }
          }
        }
        if (CHData?.qualifications && CHData.qualifications.length !== 0) {
          for (let i = 0; i < CHData.qualifications.length; i++) {
            // 更新処理
            await patchToggleQualification(CHData.qualifications[i], false, true, user?.user_id);
          }
        }

        // 職歴
        if (response.careers && response.careers.length !== 0) {
          // 既に登録されていれば削除
          const reCareers = response.careers;
          for (let i = 0; i < reCareers.length; i++) {
            if (
              CHData?.careers &&
              CHData.careers.filter((career) => career.c_id === reCareers[i].c_id).length === 0
            ) {
              await removeCareer(reCareers[i].c_id);
            }
          }
        }
        if (CHData?.careers && CHData.careers.length !== 0) {
          for (let i = 0; i < CHData.careers.length; i++) {
            // 更新処理
            await patch(CHData.careers[i]);
          }
        }

        // スキル
        if (response.skills && response.skills.length !== 0) {
          // 既に登録されていれば削除
          const reSkills = response.skills;
          for (let i = 0; i < reSkills.length; i++) {
            if (
              reSkills[i].id &&
              CHData?.skills &&
              CHData.skills.filter((skill) => skill.id === reSkills[i].id).length === 0
            ) {
              await deleteSkill(reSkills[i].id as number);
            }
          }
        }

        await patchCareerHistoryImmediately(CHData, false);
      }
      const error = e as AxiosError<ResponseError>;
      if (error.response) dispatch(notificationError(error.response.data.error_message));
    } finally {
      dispatch(toggleLoading(false));
    }
  };

  return (
    <AdminPage
      public_header
      header={
        <div className="sticky top-0 z-10">
          <HeaderTitle title={`${params.matchbox_id}｜入力代行【職務経歴書】`}>
            <div className="flex justify-end">
              <BaseButton
                theme="secondary"
                size="40"
                width="large"
                className="pr-10"
                form="pagesCHEdit"
              >
                保存する
              </BaseButton>
              <BaseButton theme="primary" size="40" form="pagesCHEdit" width="large">
                反映を確認する
              </BaseButton>
            </div>
          </HeaderTitle>
        </div>
      }
    >
      <div className="mbx-page-career-history mb-40 sm:mb-188">
        <div className="sticky top-60 z-10 mt-16">
          <NotificationMatchbou
            show={notification}
            showMatchbou={false}
            onClick={() => setNotification(false)}
          >
            職務経歴書に入力代行した内容はMATCHBOX登録企業にそのまま公開される可能性があるため、個人情報を入力しないようにしてください。
          </NotificationMatchbou>
        </div>
        <Column
          main={
            <>
              <FormContainer
                {...{
                  useFormProps,
                  onSubmit,
                  fetchData: CHData,
                  id: 'pagesCHEdit',
                  setUpdatedKey,
                  setBeforeUnload,
                  watchInitData: false,
                  methods: methods as UseFormReturn<FieldValues, Record<string, unknown>>,
                }}
              >
                <div id="summary" className="pt-56">
                  <TitleSectionTitle type="large" className="mt-12">
                    <div className="flex items-center">
                      <span className="mr-4">職務経歴概要</span>
                    </div>
                  </TitleSectionTitle>

                  <div className="space-y-8 my-22">
                    <div className="flex items-center">
                      <FormLabel label="氏名" type="small" />
                    </div>
                    <p className="text-14_20 mkt_mask_items">{user?.name}</p>
                  </div>

                  <div className="space-y-8 my-22">
                    <FormLabel label="肩書き" type="small" />
                    <FormContainerTextfield
                      className="mkt_mask_items"
                      name="job"
                      placeholder="例）UI/UXデザイナー"
                      onFocus={() => setBeforeUnload(true)}
                      emptyClear
                    />
                  </div>

                  <div className="space-y-8 my-22">
                    <FormLabel label="職務経歴概要" type="small" />
                    <FormContainerTextArea
                      className="mkt_mask_items"
                      rows={6}
                      maxLength={300}
                      name="description"
                      placeholder="例）専門学校を卒業し、株式会社マッチワークスに入社。Webデザイナーとして受託案件のWeb制作に4年間従事。ページデザインやHTML、CSS、JavaScript、PHPでのサイト構築を一通り経験した後、UI/UX領域にスキルの幅を広げるため、株式会社マッチラボに転職。現在は、Webデザイナー経験を活かしながら、アプリ開発のUI/UXデザイナーに従事しています。"
                      onFocus={() => setBeforeUnload(true)}
                    />
                  </div>

                  <div className="space-y-8 my-22">
                    <FormLabel label="活かせる経験・スキル・得意分野" type="small" />
                    <FormContainerTextArea
                      className="mkt_mask_items"
                      rows={6}
                      maxLength={300}
                      name="advantage"
                      placeholder={placeholder?.advantage}
                      onFocus={() => setBeforeUnload(true)}
                    />
                  </div>
                </div>

                <div id="SECTION_CAREERS">
                  <CH.Careers
                    patchCareer={setToggleCareers}
                    setDeleteCareers={setDeleteCareers}
                    setDeleteProjects={setDeleteProjects}
                  />
                </div>

                <div id="qualification">
                  <CH.Qualifications
                    patchItem={setToggleQualifications}
                    setDeleteQualifications={setDeleteQualifications}
                  />
                </div>

                <div id="skill">
                  <CH.Skills setDeleteskills={setDeleteskills} />
                </div>

                <div id="pr">
                  <TitleSectionTitle type="large" className="mt-12">
                    自己PR
                  </TitleSectionTitle>

                  <div className="space-y-8 my-22">
                    <FormContainerTextArea
                      className="mkt_mask_items"
                      rows={6}
                      maxLength={1000}
                      name="pr"
                      placeholder="例）プロデューサーやディレクターからの指示内容を単に実行するだけでなく、市場や競合の外部環境分析を自ら行い、携わるプロダクトやサービスに、今必要なUI/UXとは何かを提案し実行してきた経験が私の強みと認識しています。ユーザーにとって利便性の高い操作性も当然重要ですが、常に変化するサービスデザイン全般を意識することで、長期的にご利用いただけるサイトやアプリをご提案、構築できると考えています。"
                      onFocus={() => setBeforeUnload(true)}
                    />
                  </div>
                </div>

                {/* <div id="media">
                  <TitleSectionTitle type="large" className="mt-12">
                    職務経歴書の更新日
                  </TitleSectionTitle>

                  <CH.LastUpdate />
                </div> */}
              </FormContainer>
            </>
          }
          menu={<></>}
        />
      </div>
    </AdminPage>
  );
};
